import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useConnector,
  useCreateConnector,
  useDeleteConnector,
  useDiscoverConnector,
  useDisconnectOAuth,
  useStartOAuth,
  useUpdateConnector,
} from '@/lib/api/connectors';
import { ApiError } from '@/lib/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { useWorkspacePath } from '@/lib/workspacePath';
import { useAgents } from '@/lib/api/agents';
import { HeaderEditor, type HeaderRow } from '@/features/connectors/HeaderEditor';
import { ParameterEditor, type ParameterRow } from '@/features/connectors/ParameterEditor';
import { ChannelAllowlistEditor } from '@/features/connectors/ChannelAllowlistEditor';
import { ConnectorTestPanel } from '@/features/connectors/ConnectorTestPanel';
import { SlackActivityPanel } from '@/features/connectors/SlackActivityPanel';
import type {
  Connector,
  ConnectorType,
  GoogleCapability,
  GoogleConfigInput,
  GoogleConnector,
  HttpMethod,
  McpAuthMode,
  McpConfigInput,
  McpConnector,
  RestApiConfigInput,
  SlackConfigInput,
  SlackConnector,
} from '@/lib/api/types';

function isMcpConnector(c: Connector | undefined): c is McpConnector {
  return c?.connector_type === 'mcp';
}

function isGoogleConnector(c: Connector | undefined): c is GoogleConnector {
  return c?.connector_type === 'google';
}

function isSlackConnector(c: Connector | undefined): c is SlackConnector {
  return c?.connector_type === 'slack';
}

/** The three Google capabilities, with their human-facing form labels. */
const GOOGLE_CAPABILITIES: { value: GoogleCapability; label: string }[] = [
  { value: 'gmail_send', label: 'Send email as me' },
  { value: 'gmail_read', label: 'Read my email' },
  { value: 'drive_read', label: 'Read my Drive files' },
];

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const METHODS_WITH_BODY = new Set<HttpMethod>(['POST', 'PUT', 'PATCH']);
const AUTH_MODES: { value: McpAuthMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'headers', label: 'Static headers' },
  { value: 'oauth', label: 'OAuth (per user)' },
];

function ErrorAlert({ error, testId }: { error: unknown; testId?: string }) {
  if (!error) return null;
  return (
    <p
      role="alert"
      data-testid={testId}
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {(error as Error).message}
    </p>
  );
}

export function ConnectorFormPage() {
  // The URL segment addresses the connector by slug (spec 0013); a UUID also
  // resolves (AC-9). The detail GET resolves it once, and its UUID (`connectorId`)
  // drives every nested/action call -- discover, oauth, test, delete (ADR-0002).
  const { connectorRef } = useParams<{ connectorRef: string }>();
  const isEdit = Boolean(connectorRef);
  const navigate = useNavigate();
  const ws = useWorkspacePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConnectedBanner] = useState(() => searchParams.get('connected') === '1');

  const existing = useConnector(connectorRef);
  const connectorId = existing.data?.id;
  const createConnector = useCreateConnector();
  const updateConnector = useUpdateConnector(connectorId ?? '');
  const deleteConnector = useDeleteConnector();
  const discoverConnector = useDiscoverConnector(connectorId ?? '');
  const startOAuth = useStartOAuth(connectorId ?? '');
  const disconnectOAuth = useDisconnectOAuth(connectorId ?? '');

  const [connectorType, setConnectorType] = useState<ConnectorType>('rest_api');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // REST-only fields
  const [urlTemplate, setUrlTemplate] = useState('');
  const [method, setMethod] = useState<HttpMethod>('GET');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [parameters, setParameters] = useState<ParameterRow[]>([]);

  // MCP-only fields
  const [serverUrl, setServerUrl] = useState('');
  const [authMode, setAuthMode] = useState<McpAuthMode>('none');
  const [allowedTools, setAllowedTools] = useState<string[]>([]);

  // Google-only fields
  const [googleCapabilities, setGoogleCapabilities] = useState<GoogleCapability[]>([]);

  // Slack-only fields (spec 0011): the designated responder agent, an optional
  // channel allowlist, and the trigger enable flag.
  const agents = useAgents();
  const [slackAgentId, setSlackAgentId] = useState('');
  const [channelAllowlist, setChannelAllowlist] = useState<string[]>([]);
  const [slackEnabled, setSlackEnabled] = useState(true);

  // Shared between both types
  const [timeoutSeconds, setTimeoutSeconds] = useState(15);
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [loadedFromServer, setLoadedFromServer] = useState(false);

  // Populate the form once the existing connector loads (edit mode only).
  useEffect(() => {
    if (!existing.data || loadedFromServer) return;
    const c = existing.data;
    setName(c.name);
    setDescription(c.description ?? '');
    setConnectorType(c.connector_type);
    if (c.connector_type === 'google') {
      // Google config has no headers/timeout, only capabilities.
      setGoogleCapabilities(c.config.capabilities);
    } else if (c.connector_type === 'slack') {
      // Slack config has no headers/timeout, only the trigger routing fields.
      setSlackAgentId(c.config.agent_id);
      setChannelAllowlist(c.config.channel_allowlist ?? []);
      setSlackEnabled(c.config.enabled);
    } else if (c.connector_type === 'mcp') {
      setHeaders(
        c.config.headers.map((h) => ({
          name: h.name,
          value: '',
          secret: h.secret,
          hasValue: h.has_value,
        })),
      );
      setTimeoutSeconds(c.config.timeout_seconds);
      setServerUrl(c.config.server_url);
      setAuthMode(c.config.auth_mode);
      setAllowedTools(c.config.allowed_tools ?? []);
    } else {
      setHeaders(
        c.config.headers.map((h) => ({
          name: h.name,
          value: '',
          secret: h.secret,
          hasValue: h.has_value,
        })),
      );
      setTimeoutSeconds(c.config.timeout_seconds);
      setUrlTemplate(c.config.url_template);
      setMethod(c.config.method);
      setBodyTemplate(c.config.body_template ?? '');
      setParameters(
        c.config.parameters.map((p) => ({
          name: p.name,
          description: p.description ?? '',
          source: p.source,
          required: p.required,
          value: p.value ?? '',
        })),
      );
    }
    setLoadedFromServer(true);
  }, [existing.data, loadedFromServer]);

  // Strip the OAuth callback's `?connected=1` marker once shown.
  useEffect(() => {
    if (searchParams.has('connected')) {
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      setSearchParams(next, { replace: true });
    }
    // Only ever runs once on mount: intentionally ignores subsequent param changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methodSupportsBody = METHODS_WITH_BODY.has(method);

  function buildRestConfig(): RestApiConfigInput {
    return {
      url_template: urlTemplate,
      method,
      headers: headers.map((h) => ({
        name: h.name,
        secret: h.secret,
        // Empty + secret means "keep the stored value"; a typed value replaces it.
        value: h.value ? h.value : h.secret ? null : h.value,
      })),
      body_template: methodSupportsBody && bodyTemplate ? bodyTemplate : null,
      timeout_seconds: timeoutSeconds,
      parameters: parameters.map((p) => ({
        name: p.name,
        description: p.description || null,
        source: p.source,
        required: p.source === 'agent' ? p.required : true,
        value: p.source === 'fixed' ? p.value : null,
      })),
    };
  }

  function buildMcpConfig(): McpConfigInput {
    return {
      server_url: serverUrl,
      auth_mode: authMode,
      headers:
        authMode === 'headers'
          ? headers.map((h) => ({
              name: h.name,
              secret: h.secret,
              value: h.value ? h.value : h.secret ? null : h.value,
            }))
          : [],
      allowed_tools: allowedTools.length > 0 ? allowedTools : null,
      timeout_seconds: timeoutSeconds,
    };
  }

  function buildGoogleConfig(): GoogleConfigInput {
    return { capabilities: googleCapabilities };
  }

  function buildSlackConfig(): SlackConfigInput {
    const channels = channelAllowlist.map((c) => c.trim()).filter(Boolean);
    return {
      agent_id: slackAgentId,
      // Empty means "every channel"; send null rather than an empty list.
      channel_allowlist: channels.length > 0 ? channels : null,
      enabled: slackEnabled,
    };
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isEdit && connectorId) {
      const config =
        connectorType === 'mcp'
          ? buildMcpConfig()
          : connectorType === 'google'
            ? buildGoogleConfig()
            : connectorType === 'slack'
              ? buildSlackConfig()
              : buildRestConfig();
      updateConnector.mutate(
        { name, description: description || null, config },
        { onSuccess: () => navigate(ws('connectors')) },
      );
    } else if (connectorType === 'mcp') {
      createConnector.mutate(
        { name, description: description || null, connector_type: 'mcp', config: buildMcpConfig() },
        { onSuccess: () => navigate(ws('connectors')) },
      );
    } else if (connectorType === 'google') {
      createConnector.mutate(
        {
          name,
          description: description || null,
          connector_type: 'google',
          config: buildGoogleConfig(),
        },
        { onSuccess: () => navigate(ws('connectors')) },
      );
    } else if (connectorType === 'slack') {
      createConnector.mutate(
        {
          name,
          description: description || null,
          connector_type: 'slack',
          config: buildSlackConfig(),
        },
        { onSuccess: () => navigate(ws('connectors')) },
      );
    } else {
      createConnector.mutate(
        { name, description: description || null, connector_type: 'rest_api', config: buildRestConfig() },
        { onSuccess: () => navigate(ws('connectors')) },
      );
    }
  }

  function handleDelete() {
    if (!connectorId) return;
    deleteConnector.mutate(connectorId, { onSuccess: () => navigate(ws('connectors')) });
  }

  function toggleAllowedTool(toolName: string, checked: boolean) {
    setAllowedTools((prev) => (checked ? [...prev, toolName] : prev.filter((t) => t !== toolName)));
  }

  function toggleGoogleCapability(capability: GoogleCapability, checked: boolean) {
    setGoogleCapabilities((prev) =>
      checked ? [...prev, capability] : prev.filter((c) => c !== capability),
    );
  }

  function handleConnect() {
    startOAuth.mutate(undefined, {
      onSuccess: (res) => window.location.assign(res.authorization_url),
    });
  }

  function handleSlackDisconnect() {
    if (
      !window.confirm(
        'Disconnect this Slack workspace? The agent stops responding in Slack until it is reinstalled.',
      )
    ) {
      return;
    }
    disconnectOAuth.mutate();
  }

  const mutation = isEdit ? updateConnector : createConnector;
  const agentParameters = parameters
    .filter((p) => p.source === 'agent')
    .map((p) => ({ name: p.name, required: p.required }));
  const mcpConnector = isMcpConnector(existing.data) ? existing.data : null;
  const googleConnector = isGoogleConnector(existing.data) ? existing.data : null;
  const slackConnector = isSlackConnector(existing.data) ? existing.data : null;
  // The per-environment capability gate is surfaced on the connector row, so it
  // is only known once an existing google connector has loaded.
  const platformDisabledCapabilities = googleConnector?.platform_disabled_capabilities ?? [];
  // Require at least one capability before a google connector can be saved.
  const googleInvalid = connectorType === 'google' && googleCapabilities.length === 0;
  // A slack connector must name its designated responder agent.
  const slackInvalid = connectorType === 'slack' && !slackAgentId;

  if (isEdit && existing.isLoading) {
    return <Spinner label="Loading connector" />;
  }

  // An unknown connector slug/UUID under the active workspace is a not-found
  // state (spec 0013, AC-11) -- resolution stays inside the workspace RLS session.
  if (isEdit && existing.error) {
    const notFound = existing.error instanceof ApiError && existing.error.status === 404;
    return (
      <div
        role="alert"
        data-testid="connector-not-found"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
      >
        <p>{notFound ? 'Connector not found.' : 'Failed to load this connector.'}</p>
        <Link
          to={ws('connectors')}
          className="mt-2 inline-block font-medium text-brand-700 hover:underline"
        >
          Back to connectors
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card title={isEdit ? 'Edit connector' : 'New connector'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="connector-name-input"
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="connector-description-input"
          />

          {!isEdit && (
            <Select
              label="Type"
              value={connectorType}
              onChange={(e) => setConnectorType(e.target.value as ConnectorType)}
              data-testid="connector-type-select"
            >
              <option value="rest_api">REST API</option>
              <option value="mcp">MCP server</option>
              <option value="google">Google</option>
              <option value="slack">Slack</option>
            </Select>
          )}

          {connectorType === 'rest_api' && (
            <>
              <Input
                label="URL"
                value={urlTemplate}
                onChange={(e) => setUrlTemplate(e.target.value)}
                placeholder="https://example.com/users/<user_id>"
                required
                data-testid="connector-url-input"
              />
              <Select
                label="Method"
                value={method}
                onChange={(e) => setMethod(e.target.value as HttpMethod)}
                data-testid="connector-method-select"
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
              {methodSupportsBody && (
                <Textarea
                  label="Body template"
                  value={bodyTemplate}
                  onChange={(e) => setBodyTemplate(e.target.value)}
                  rows={3}
                  placeholder={'{"user_name": "<user_name>"}'}
                  data-testid="connector-body-input"
                />
              )}
              <Input
                label="Timeout (seconds)"
                type="number"
                min={1}
                max={60}
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
                data-testid="connector-timeout-input"
              />
              <ParameterEditor rows={parameters} onChange={setParameters} />
              <HeaderEditor rows={headers} onChange={setHeaders} />
            </>
          )}

          {connectorType === 'mcp' && (
            <>
              <Input
                label="Server URL"
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="https://mcp.example.com"
                required
                data-testid="connector-server-url-input"
              />
              <Select
                label="Auth mode"
                value={authMode}
                onChange={(e) => setAuthMode(e.target.value as McpAuthMode)}
                data-testid="connector-auth-mode-select"
              >
                {AUTH_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
              <Input
                label="Timeout (seconds)"
                type="number"
                min={1}
                max={120}
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
                data-testid="connector-timeout-input"
              />
              {authMode === 'headers' && <HeaderEditor rows={headers} onChange={setHeaders} />}
            </>
          )}

          {connectorType === 'google' && (
            <fieldset className="space-y-2" data-testid="google-capabilities">
              <legend className="mb-1 block text-sm font-medium text-gray-700">Capabilities</legend>
              {GOOGLE_CAPABILITIES.map((cap) => {
                const gated = platformDisabledCapabilities.includes(cap.value);
                return (
                  <div key={cap.value} data-testid={`google-capability-${cap.value}`}>
                    <label
                      className={cn(
                        'flex items-center gap-2 text-sm',
                        gated ? 'text-gray-400' : 'text-gray-700',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                        checked={googleCapabilities.includes(cap.value)}
                        disabled={gated}
                        onChange={(e) => toggleGoogleCapability(cap.value, e.target.checked)}
                        data-testid={`google-capability-checkbox-${cap.value}`}
                      />
                      {cap.label}
                    </label>
                    {gated && (
                      <p
                        className="ml-6 text-xs text-gray-500"
                        data-testid={`google-capability-gated-${cap.value}`}
                      >
                        Not enabled by the platform in this environment.
                      </p>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-gray-500">
                Select at least one capability. Each teammate connects their own Google account, and
                the agent can only use what they consent to.
              </p>
            </fieldset>
          )}

          {connectorType === 'slack' && (
            <>
              <Select
                label="Responder agent"
                value={slackAgentId}
                onChange={(e) => setSlackAgentId(e.target.value)}
                required
                data-testid="slack-agent-select"
              >
                <option value="">Select an agent</option>
                {(agents.data ?? []).map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-500">
                The one agent that answers messages from this Slack workspace. Slack messages only
                choose the conversation; the responder is always this agent.
              </p>
              <ChannelAllowlistEditor rows={channelAllowlist} onChange={setChannelAllowlist} />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={slackEnabled}
                  onChange={(e) => setSlackEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  data-testid="slack-enabled-toggle"
                />
                Enabled (incoming Slack messages trigger this agent)
              </label>
            </>
          )}

          <ErrorAlert error={mutation.error} />

          <div className="flex items-center justify-between">
            <Button
              type="submit"
              disabled={mutation.isPending || googleInvalid || slackInvalid}
              data-testid="save-connector-button"
            >
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save changes' : 'Create connector'}
            </Button>
            {isEdit && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                data-testid="delete-connector-button"
              >
                Delete
              </Button>
            )}
          </div>
        </form>
      </Card>

      <div className="space-y-6">
        {isEdit && connectorType === 'rest_api' && connectorId && (
          <ConnectorTestPanel connectorId={connectorId} agentParameters={agentParameters} />
        )}

        {mcpConnector && connectorId && (
          <Card
            title="Discover tools"
            description="Connects to the server and lists the tools it exposes."
          >
            <Button
              type="button"
              variant="secondary"
              onClick={() => discoverConnector.mutate()}
              disabled={discoverConnector.isPending}
              data-testid="discover-connector-button"
            >
              {discoverConnector.isPending ? 'Discovering...' : 'Discover tools'}
            </Button>
            <div className="mt-3">
              <ErrorAlert error={discoverConnector.error} testId="discover-error" />
            </div>
            {discoverConnector.data?.truncated && (
              <p className="mt-2 text-xs text-amber-700">
                The server exposes more tools than the 50-tool cap; showing the first 50.
              </p>
            )}

            {(() => {
              // Prefer the just-discovered list for instant feedback (no
              // refetch round-trip needed); fall back to the persisted
              // inventory on a fresh page load.
              const tools = discoverConnector.data?.tools ?? mcpConnector.config.tool_inventory;
              if (tools === null || tools === undefined) {
                return (
                  <p className="mt-3 text-xs text-gray-500">Run discover to see available tools.</p>
                );
              }
              return (
                <>
                  <ul className="mt-3 space-y-2" data-testid="mcp-tool-list">
                    {tools.map((tool) => (
                      <li
                        key={tool.name}
                        className="flex items-start gap-2 text-sm"
                        data-testid="mcp-tool-row"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          checked={allowedTools.includes(tool.name)}
                          onChange={(e) => toggleAllowedTool(tool.name, e.target.checked)}
                          data-testid="mcp-tool-checkbox"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{tool.name}</p>
                          {tool.description && (
                            <p className="text-xs text-gray-500">{tool.description}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-gray-500">
                    Checked tools are offered to agents; none checked means all tools are offered. Save
                    changes to apply.
                  </p>
                </>
              );
            })()}
          </Card>
        )}

        {mcpConnector && mcpConnector.config.auth_mode === 'oauth' && connectorId && (
          <Card
            title="Your connection"
            description="Each teammate connects their own account; nobody else can use it."
          >
            {showConnectedBanner && (
              <p
                className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
                data-testid="oauth-connected-banner"
              >
                Account connected.
              </p>
            )}
            <p className="mb-3 text-sm text-gray-700" data-testid="mcp-grant-status">
              Status: {mcpConnector.my_grant_status}
            </p>
            {mcpConnector.my_grant_status === 'connected' ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => disconnectOAuth.mutate()}
                disabled={disconnectOAuth.isPending}
                data-testid="disconnect-oauth-button"
              >
                {disconnectOAuth.isPending ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleConnect}
                disabled={startOAuth.isPending}
                data-testid="connect-oauth-button"
              >
                {startOAuth.isPending ? 'Connecting...' : 'Connect your account'}
              </Button>
            )}
            <div className="mt-3 space-y-2">
              <ErrorAlert error={startOAuth.error} testId="oauth-start-error" />
              <ErrorAlert error={disconnectOAuth.error} testId="oauth-disconnect-error" />
            </div>
          </Card>
        )}

        {googleConnector && connectorId && (
          <Card
            title="Your connection"
            description="Connect your own Google account; nobody else in the workspace can use it."
          >
            {showConnectedBanner && (
              <p
                className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
                data-testid="google-connected-banner"
              >
                Account connected.
              </p>
            )}
            <p className="mb-3 text-sm text-gray-700" data-testid="google-grant-status">
              Status: {googleConnector.my_grant_status}
            </p>
            {googleConnector.my_grant_status === 'connected' && (
              <p className="mb-3 text-sm text-gray-700" data-testid="google-connected-account">
                Connected as {googleConnector.external_account}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {googleConnector.my_grant_status === 'none' && (
                <Button
                  type="button"
                  onClick={handleConnect}
                  disabled={startOAuth.isPending}
                  data-testid="google-connect-button"
                >
                  {startOAuth.isPending ? 'Connecting...' : 'Connect your account'}
                </Button>
              )}
              {googleConnector.my_grant_status === 'connected' && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => disconnectOAuth.mutate()}
                  disabled={disconnectOAuth.isPending}
                  data-testid="google-disconnect-button"
                >
                  {disconnectOAuth.isPending ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              )}
              {googleConnector.missing_capabilities.length > 0 && (
                <Button
                  type="button"
                  onClick={handleConnect}
                  disabled={startOAuth.isPending}
                  data-testid="google-reconnect-button"
                >
                  {startOAuth.isPending ? 'Connecting...' : 'Reconnect'}
                </Button>
              )}
            </div>
            {googleConnector.missing_capabilities.length > 0 && (
              <p className="mt-2 text-xs text-amber-700" data-testid="google-missing-capabilities">
                Reconnect to grant the newly configured access:{' '}
                {googleConnector.missing_capabilities.join(', ')}.
              </p>
            )}
            <div className="mt-3 space-y-2">
              <ErrorAlert error={startOAuth.error} testId="google-oauth-start-error" />
              <ErrorAlert error={disconnectOAuth.error} testId="google-oauth-disconnect-error" />
            </div>
          </Card>
        )}

        {slackConnector && connectorId && (
          <Card
            title="Slack workspace"
            description="Install this connector into Slack so the agent can respond to messages there. The install is workspace-wide: everyone on your team shares it."
          >
            {showConnectedBanner && (
              <p
                className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
                data-testid="slack-connected-banner"
              >
                Slack workspace connected.
              </p>
            )}
            {slackConnector.slack_install?.installed ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700" data-testid="slack-install-status">
                  Installed
                  {slackConnector.slack_install.workspace_name
                    ? ` in ${slackConnector.slack_install.workspace_name}`
                    : ''}
                  .
                </p>
                {slackConnector.slack_install.since && (
                  <p className="text-xs text-gray-500" data-testid="slack-installed-since">
                    Since {new Date(slackConnector.slack_install.since).toLocaleString()}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleConnect}
                    disabled={startOAuth.isPending}
                    data-testid="slack-reinstall-button"
                  >
                    {startOAuth.isPending ? 'Redirecting...' : 'Reinstall'}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleSlackDisconnect}
                    disabled={disconnectOAuth.isPending}
                    data-testid="slack-disconnect-button"
                  >
                    {disconnectOAuth.isPending ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                onClick={handleConnect}
                disabled={startOAuth.isPending}
                data-testid="slack-install-button"
              >
                {startOAuth.isPending ? 'Redirecting...' : 'Add to Slack'}
              </Button>
            )}
            <div className="mt-3 space-y-2">
              <ErrorAlert error={startOAuth.error} testId="slack-oauth-start-error" />
              <ErrorAlert error={disconnectOAuth.error} testId="slack-oauth-disconnect-error" />
            </div>
          </Card>
        )}

        {slackConnector && connectorId && (
          <SlackActivityPanel connectorId={connectorId} agentId={slackConnector.config.agent_id} />
        )}
      </div>
    </div>
  );
}
